import { computed, type Ref } from "vue";
import { storeToRefs } from "pinia";
import { useResultsV2Store } from "@stores/survey/resultsV2/resultsV2";
import { useCardAttributes } from "@utils/composables/questionCard";
import type { SelectOption } from "@/components/episto-ui/select";

export const CUSTOM_BASE_ELIGIBLE_TYPES = [
  "q_single", "q_multiple", "q_ranking", "q_rating", "q_slider", "q_image_choice", "q_image_choice_multiple", "q_nps",
];
export const CLOSED_QUESTION_CARD_TYPES = [...CUSTOM_BASE_ELIGIBLE_TYPES, "q_matrix", "q_language", "o_ab", "o_cells"];

interface AvailableCard {
  id: number;
  name: string;
  type: string;
  is_admin?: boolean;
  specs?: { title?: Record<string, string>; transformation_type?: string; answers?: unknown[] };
  questions?: { id: number }[];
}

// Hidden cards are closed only when they carry declared answers (URL parameters do not always).
const isClosedHiddenCard = (card: AvailableCard) => {
  const transformationType = card.specs?.transformation_type;
  if (transformationType === "Single Match (Matrix)" || transformationType === "Multiple Match (Matrix)") return false;
  if (transformationType === "Mailing contact attribute (Open)") return false;
  if (transformationType === "URL parameter" && !card.specs?.answers?.length) return false;
  return true;
};

export function useExportQuestionOptions(adminMode: Ref<boolean>) {
  const { availableCards } = storeToRefs(useResultsV2Store());
  const { cardsTitle } = useCardAttributes();

  const cards = computed<AvailableCard[]>(() =>
    (availableCards.value as AvailableCard[]).filter((card) => !card.is_admin || adminMode.value),
  );

  const cardLabel = (card: AvailableCard): string => {
    const title = cardsTitle(card);
    return title ? `${title} · ${card.name}` : card.name;
  };
  const questionOption = (card: AvailableCard): SelectOption => ({ value: card.questions![0].id, label: cardLabel(card) });

  const closedQuestionOptions = computed<SelectOption[]>(() =>
    cards.value
      .filter((card) => CLOSED_QUESTION_CARD_TYPES.includes(card.type) || (card.type === "q_hidden" && isClosedHiddenCard(card)))
      .filter((card) => card.questions?.[0]?.id)
      .map(questionOption),
  );
  const baseQuestionOptions = computed<SelectOption[]>(() =>
    cards.value.filter((card) => CUSTOM_BASE_ELIGIBLE_TYPES.includes(card.type) && card.questions?.[0]?.id).map(questionOption),
  );
  const questionNameOptions = computed<SelectOption[]>(() =>
    cards.value.map((card) => ({ value: card.name, label: cardLabel(card) })),
  );

  return { closedQuestionOptions, baseQuestionOptions, questionNameOptions };
}
